import React, { useState, useEffect, useContext, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

// Types
import HostTarget from "../../types/host/HostTarget";
import InstanceTarget from "../../types/instance/InstanceTarget";
import CombinedChanges from "../../types/instance/CombinedChanges";
import ChartPlotLine from "../../types/ChartPlotLine";
import HostStatisticV2 from "../../types/host/HostStatisticV2";

// Components
import WidgetCard from "../../component/instance/widget";
import ProcessorChart from "../../component/host/chart/ProcessorChart";
import MemoryChart from "../../component/host/chart/MemoryChart";
import DiskChart from "../../component/host/chart/DiskChart";
import DiskUtilisationChart from "../../component/host/chart/DiskUtilisationChart";
import HostsTabbed from "./HostsTabbed";

// Helpers
import Helper from "../../helpers/Helper";

// API
import HostApi from "../../api/host/HostApi";
import EventsApi from "../../api/instance/EventsApi";

// Constants
import { DATA_LOADED, DATA_LOADING } from "../../component/Constants";

// Contexts
import { TimeRangeContext } from "../../context/TimeRangeContext";
import { buildRedirectQuery, buildApiQuery } from "../../helpers/timeRangeUtils";
import { useEventTypes } from "../../context/EventTypeContext";

interface IListProps {
    instances: InstanceTarget[],
    hosts: HostTarget[],
    toggleMenu: Function,
}

const Detail: React.FC<IListProps> = (props) => {
    const [loading, setLoading] = useState<number>(DATA_LOADING)

    const [averageProcessorMetric, setAverageProcessorMetric] = useState<number>(0)
    const [previousAverageProcessorMetric, setPreviousAverageProcessorMetric] = useState<number | null>(0)
    const [averageMemoryMetric, setAverageMemoryMetric] = useState<number>(0)
    const [previousAverageMemoryMetric, setPreviousAverageMemoryMetric] = useState<number | null>(0)
    const [averageDiskMetric, setAverageDiskMetric] = useState<number | null>(0)
    const [previousAverageDiskMetric, setPreviousAverageDiskMetric] = useState<number | null>(0)

    const [chartPlotLines, setChartPlotLines] = useState<ChartPlotLine[]>([])

    const timeRangeContext = useContext(TimeRangeContext)
    const { eventTypes } = useEventTypes();
    const navigate = useNavigate();
    const { hostId } = useParams();
    const { hosts } = props;
    const filteredHosts: HostTarget[] = hosts.filter(host => Number(host.id) === Number(hostId));
    const host = filteredHosts[0]

    const comparisonSupported = useMemo(() => {
        return Helper.isComparisonSupported(timeRangeContext)
    }, [timeRangeContext.timeRange.from.valueOf(), timeRangeContext.timeRange.to.valueOf()]);

    const loadData = async() => {
        if (filteredHosts.length === 0) {
            navigate('/not-found');
            return;
        }

        const hostIds = [filteredHosts[0].id];
        const query = buildApiQuery(timeRangeContext.timeRange);
        const prevQuery = buildApiQuery(timeRangeContext.timeRange, true);

        const promises: Promise<any>[] = [
            HostApi.getHostStatisticV2Summary(query, hostIds),
            EventsApi.getEventsChangesAndAlertsForHost(timeRangeContext, Number(filteredHosts[0].id)),
        ];
        if (comparisonSupported) {
            promises.push(HostApi.getHostStatisticV2Summary(prevQuery, hostIds));
        }

        try {
            const results = await Promise.all(promises);
            const current: HostStatisticV2 | undefined = results[0][0];
            const events = results[1] || [];
            const previous: HostStatisticV2 | undefined = comparisonSupported ? results[2]?.[0] : undefined;

            setAverageProcessorMetric(current ? Math.round(Number(current.cpuutilisation) || 0) : 0);
            setAverageMemoryMetric(current ? Math.round(Number(current.memoryutilisation) || 0) : 0);
            setAverageDiskMetric(current ? Math.round(Number(current.ioutilisation) || 0) : 0);

            if (comparisonSupported) {
                setPreviousAverageProcessorMetric(previous ? Math.round(Number(previous.cpuutilisation) || 0) : null);
                setPreviousAverageMemoryMetric(previous ? Math.round(Number(previous.memoryutilisation) || 0) : null);
                setPreviousAverageDiskMetric(previous ? Math.round(Number(previous.ioutilisation) || 0) : null);
            } else {
                setPreviousAverageProcessorMetric(null);
                setPreviousAverageMemoryMetric(null);
                setPreviousAverageDiskMetric(null);
            }

            // Change/event plot lines for the per-host charts.
            const combinedChanges: CombinedChanges[] = events.map((currentItem: any, index: number) => ({
                id: index,
                instanceId: currentItem.datasourceid,
                hostId: currentItem.hostid,
                timeslice: currentItem.timeslice,
                [currentItem.event]: currentItem,
            }));
            combinedChanges.sort((a, b) => {
                const aa = Helper.parseTimesliceWithTz(String(a.timeslice)).valueOf();
                const bb = Helper.parseTimesliceWithTz(String(b.timeslice)).valueOf();
                return bb - aa;
            });
            const interval = Helper.getInterval(timeRangeContext.timeRange?.from, timeRangeContext.timeRange?.to, timeRangeContext.timeRange?.interval);
            const chartCategories = Helper.getChartAxisCategories(timeRangeContext.timeRange?.from, timeRangeContext.timeRange?.to, interval);
            setChartPlotLines(Helper.getChartPlotLines(buildRedirectQuery(timeRangeContext.timeRange), combinedChanges, chartCategories, interval, false, undefined, eventTypes));

            setLoading(DATA_LOADED);
        } catch (error) {
            console.error('Failed to load host detail data.', error);
            setLoading(DATA_LOADED);
        }
    }

    useEffect(() => {
        setLoading(DATA_LOADING)
        void loadData();
    }, [hostId, timeRangeContext]);

    const refresh = () => {
        setLoading(DATA_LOADING)
        void loadData();
    }

    if (!host) {
        return null;
    }

    return (
        <HostsTabbed
            hosts={props.hosts}
            toggleMenu={props.toggleMenu}

            refresh={refresh}
            loading={loading}
            activeTab="metrics"
            title={`${host.name || ''} - DBmarlin`}
            description="Host performance detail view, showing key performance metrics and detailed executed statement and waits information"
        >

            {/* Metric Widgets - Host */}
            <div className="row row-cols-1 row-cols-sm-2 row-cols-lg-3">
                <WidgetCard
                    name="CPU Average"
                    type='percent'
                    iconClassName="processor"
                    icon="fa-microchip"
                    value={averageProcessorMetric}
                    previous={previousAverageProcessorMetric ?? undefined}
                    cardTooltipText="The average CPU utilization"
                    loading={loading === DATA_LOADING}
                />
                <WidgetCard
                    name="Memory Average"
                    type='percent'
                    iconClassName="memory"
                    icon="fa-memory"
                    value={averageMemoryMetric}
                    previous={previousAverageMemoryMetric ?? undefined}
                    cardTooltipText="The average memory utilization"
                    loading={loading === DATA_LOADING}
                />
                <WidgetCard
                    name="Disk I/O"
                    type='percent'
                    iconClassName="io"
                    icon="fa-hdd"
                    value={averageDiskMetric ?? 0}
                    previous={previousAverageDiskMetric ?? undefined}
                    cardTooltipText="The average disk I/O utilization"
                    loading={loading === DATA_LOADING}
                />
            </div>

            {/* Charts */}
            <div className="row">
                <div className="col">
                    {Boolean(process.env.REACT_APP_HOST_CHARTS) && loading === DATA_LOADED && (
                        <React.Fragment>
                            <ProcessorChart host={host} plotLines={chartPlotLines} />
                            <MemoryChart host={host} plotLines={chartPlotLines} />
                            <DiskChart host={host} plotLines={chartPlotLines} />
                            <DiskUtilisationChart host={host} plotLines={chartPlotLines} />
                        </React.Fragment>
                    )}
                </div>
            </div>
        </HostsTabbed>
    );
};

export default Detail;
